/**
 * BulkImportChurchesDialog
 * ------------------------
 * Paste CSV/TSV → preview → commit. Reached from /admin/churches "Bulk Import"
 * button in ChurchesManagement.
 *
 * Backend: POST /api/admin/churches/bulk-import
 *   body: { text: "csv or tsv string", dry_run: bool }
 *   returns: { created:[...], skipped:[...], totals:{parsed, created, errors} }
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { churchesAPI, getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Loader2, Upload } from 'lucide-react';

const SAMPLE = `name,organisation_type,parent_name,countries,country,city,code,leader_name,leader_title
Zimbabwe,country,FWM HQ,,Zimbabwe,,CTR-ZW,,
Harare Province,province,Zimbabwe,,Zimbabwe,Harare,PRV-HRE,Moses Banda,Provincial Overseer
Nordic Province,province,FWM HQ,Sweden;Norway;Denmark,Sweden,,PRV-NORDIC,,
Avondale Assembly,assembly,Harare Province,,Zimbabwe,Harare,,Grace Moyo,Resident Pastor
Mbare Home Group,home_group,Avondale Assembly,,Zimbabwe,Harare,,,`;

const downloadRejects = (rows) => {
  const header = 'row,name,reason\n';
  const body = rows.map((r) =>
    [r.row, `"${(r.name || '').replace(/"/g, '""')}"`, `"${(r.message || '').replace(/"/g, '""')}"`]
      .join(',')).join('\n');
  const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `organisation-import-rejects-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const BulkImportChurchesDialog = ({ open, onOpenChange }) => {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState(null);

  const runImport = useMutation({
    mutationFn: () => churchesAPI.bulkImport({ text, dry_run: dryRun }),
    onSuccess: (res) => {
      setResult(res.data);
      if (!dryRun) {
        qc.invalidateQueries(['admin-churches']);
        qc.invalidateQueries({ queryKey: ['hx-tree'] });
        toast.success(`Imported ${res.data.totals.created} church${res.data.totals.created === 1 ? '' : 'es'}.`);
      }
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Bulk import failed.')),
  });

  const reset = () => {
    setText('');
    setDryRun(true);
    setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl" data-testid="bulk-import-churches-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-emerald-600" />
            Bulk Import Churches
          </DialogTitle>
          <DialogDescription>
            Paste a CSV (or TSV copied straight from Excel/Sheets). First row must
            be the header. <span className="font-medium">name</span> is required;
            the rest are optional. Parents are matched by name — the row can point
            at another row in the same paste (by <span className="font-medium">parent_name</span> or
            <span className="font-medium"> parent_code</span>), or at an organisation already saved.
            Use <span className="font-medium">organisation_type</span> (global_ministry, country,
            province, district, assembly, bible_college_campus, ministry, home_group, cell) and a
            semicolon-separated <span className="font-medium">countries</span> column for provinces
            that span several nations. Rows that break the hierarchy rules are rejected individually —
            everything valid still imports.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">CSV / TSV</Label>
              <button
                type="button"
                onClick={() => setText(SAMPLE)}
                className="text-[11px] text-emerald-700 hover:underline"
                data-testid="bulk-import-fill-sample"
              >
                Fill with sample rows
              </button>
            </div>
            <Textarea
              value={text}
              onChange={(e) => { setText(e.target.value); setResult(null); }}
              rows={10}
              className="font-mono text-xs"
              placeholder="name,level,parent_name,country,city,region,contact_email,contact_phone"
              data-testid="bulk-import-textarea"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Recognised columns: <code>name</code>, <code>level</code>,{' '}
              <code>parent_name</code>, <code>country</code>, <code>region</code>,{' '}
              <code>district</code>, <code>city</code>, <code>contact_email</code>,{' '}
              <code>contact_phone</code>, <code>currency</code>. Levels: hq,
              province, district, assembly, ministry, home_group, cell.
            </p>
          </div>

          <div className="flex items-center gap-3 rounded-md bg-slate-50 px-3 py-2">
            <Switch
              id="bulk-dry-run"
              checked={dryRun}
              onCheckedChange={setDryRun}
              data-testid="bulk-import-dry-run"
            />
            <Label htmlFor="bulk-dry-run" className="text-sm cursor-pointer">
              Dry run — preview only, nothing is saved
            </Label>
          </div>

          {result && (
            <div className="rounded-md border bg-white p-3" data-testid="bulk-import-result">
              <div className="flex items-center gap-2 text-sm mb-2">
                {result.totals.errors === 0 ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                )}
                <span className="font-medium">
                  {result.dry_run ? 'Preview' : 'Result'}:
                </span>
                <Badge variant="outline">{result.totals.parsed} parsed</Badge>
                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                  {result.totals.created} {result.dry_run ? 'would create' : 'created'}
                </Badge>
                {result.totals.errors > 0 && (
                  <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">
                    {result.totals.errors} skipped
                  </Badge>
                )}
              </div>

              {(result.created || []).length > 0 && (
                <GroupedPreview rows={result.created} />
              )}

              {(result.skipped || []).length > 0 && (
                <div className="mt-2 text-xs" data-testid="bulk-import-errors">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium text-rose-700">
                      Rejected rows ({result.skipped.length}) — the valid rows
                      {result.dry_run ? ' will still import' : ' were imported'}
                    </div>
                    <button
                      type="button"
                      onClick={() => downloadRejects(result.skipped)}
                      className="text-[11px] text-rose-700 hover:underline"
                      data-testid="bulk-import-download-rejects"
                    >
                      Download rejects CSV
                    </button>
                  </div>
                  <ul className="space-y-0.5">
                    {result.skipped.map((e) => (
                      <li key={`err-${e.row}`} className="text-rose-700">
                        Row {e.row}{e.name ? ` · ${e.name}` : ''}: {e.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-import-cancel">
            Close
          </Button>
          <Button
            onClick={() => runImport.mutate()}
            disabled={!text.trim() || runImport.isPending}
            data-testid="bulk-import-run"
          >
            {runImport.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {dryRun ? 'Preview' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportChurchesDialog;


/**
 * Preview table grouped by `country`. Sorted alphabetically with
 * "(no country)" bucket rendered last so admins spot regional gaps
 * before committing a large paste. Each group is collapsible — first
 * 3 are open by default so a big paste (10+ countries) stays scannable.
 */
function GroupedPreview({ rows }) {
  const groups = React.useMemo(() => {
    const bucket = new Map();
    (rows || []).forEach((r) => {
      const key = (r.country || '').trim() || '__NONE__';
      if (!bucket.has(key)) bucket.set(key, []);
      bucket.get(key).push(r);
    });
    return Array.from(bucket.entries()).sort(([a], [b]) => {
      if (a === '__NONE__') return 1;
      if (b === '__NONE__') return -1;
      return a.localeCompare(b);
    });
  }, [rows]);

  // Default: first 3 groups open, rest collapsed. Keyed by country code
  // so state survives Preview → Import transitions on the same paste.
  const [openMap, setOpenMap] = useState(() => {
    const initial = {};
    groups.forEach(([country], idx) => { initial[country] = idx < 3; });
    return initial;
  });
  React.useEffect(() => {
    setOpenMap((prev) => {
      const next = { ...prev };
      groups.forEach(([country], idx) => {
        if (!(country in next)) next[country] = idx < 3;
      });
      return next;
    });
  }, [groups]);

  const toggle = (country) => setOpenMap((m) => ({ ...m, [country]: !m[country] }));
  const expandAll = () => setOpenMap(Object.fromEntries(groups.map(([c]) => [c, true])));
  const collapseAll = () => setOpenMap(Object.fromEntries(groups.map(([c]) => [c, false])));

  return (
    <div className="text-xs" data-testid="bulk-import-preview">
      {groups.length > 1 && (
        <div className="flex items-center justify-end gap-3 mb-1">
          <button
            type="button"
            onClick={expandAll}
            className="text-[10px] text-emerald-700 hover:underline"
            data-testid="bulk-import-expand-all"
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="text-[10px] text-slate-500 hover:underline"
            data-testid="bulk-import-collapse-all"
          >
            Collapse all
          </button>
        </div>
      )}
      <div className="max-h-56 overflow-y-auto space-y-2">
        {groups.map(([country, groupRows]) => {
          const label = country === '__NONE__' ? '(no country)' : country;
          const testKey = country === '__NONE__' ? 'none' : country.toLowerCase().replace(/\s+/g, '-');
          const isOpen = !!openMap[country];
          return (
            <div key={country} data-testid={`bulk-import-group-${testKey}`}>
              <button
                type="button"
                onClick={() => toggle(country)}
                className="w-full flex items-center gap-2 border-b border-slate-200 pb-1 mb-1 hover:bg-slate-50 rounded-sm px-1"
                data-testid={`bulk-import-group-toggle-${testKey}`}
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  <ChevronDown className="h-3 w-3 text-slate-500" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-slate-500" />
                )}
                <span className="font-semibold text-slate-700">{label}</span>
                <span className="text-[10px] text-slate-500">
                  {groupRows.length} church{groupRows.length === 1 ? '' : 'es'}
                </span>
              </button>
              {isOpen && (
                <table className="w-full">
                  <thead className="text-slate-400">
                    <tr className="text-left">
                      <th className="pr-2 font-normal">Row</th>
                      <th className="pr-2 font-normal">Name</th>
                      <th className="pr-2 font-normal">Level</th>
                      <th className="pr-2 font-normal">City</th>
                      <th className="pr-2 font-normal">Parent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupRows.map((r) => (
                      <tr key={`${r.row}-${r.name}`} className="border-t border-slate-100">
                        <td className="py-1 pr-2 text-slate-500">{r.row}</td>
                        <td className="py-1 pr-2 font-medium">{r.name}</td>
                        <td className="py-1 pr-2">{r.level}</td>
                        <td className="py-1 pr-2 text-slate-600">{r.city || '—'}</td>
                        <td className="py-1 pr-2 text-slate-600">
                          {r.parent_name || (r.parent_id ? '(existing)' : '(HQ default)')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
